<script lang="ts">
  import { faPlay } from '@fortawesome/free-solid-svg-icons'
  import Icon from './Icon.svelte'
  import { getI18n } from '../i18n'
  import { attachStream } from '../utils/hls'
  import { formatDuration } from '../utils/duration'
  import type { SerializedVideo } from '../types'

  // `autoplay` starts the video without the play button, e.g. when it was picked from a playlist.
  // `priority` loads the poster first, for a player at the top of the page.
  let { video, autoplay = false, priority = false }: { video: SerializedVideo; autoplay?: boolean; priority?: boolean } = $props()

  const i18n = getI18n()
  const sizes = '(max-width: 744px) 100vw, 720px'

  let playRequested = $state(false)
  let videoElement = $state<HTMLVideoElement>()
  const started = $derived(autoplay || playRequested)

  $effect(() => {
    if (!videoElement) return

    const element = videoElement
    // The play button the focus was on is gone, so keyboard users continue on the player's controls.
    if (playRequested) element.focus()
    let releaseStream: (() => void) | undefined
    let cancelled = false

    attachStream(element, video.playlistUrl).then((release) => {
      if (cancelled) return release()

      releaseStream = release
      // Browsers may refuse to start playback (e.g. power saving); the native controls stay usable then.
      element.play().catch(() => {})
    })

    return () => {
      cancelled = true
      element.pause()
      releaseStream?.()
    }
  })
</script>

<div class="video-player">
  {#if started}
    <!-- svelte-ignore a11y_media_has_caption -- captions are part of the HLS stream -->
    <video class="video-player-video" bind:this={videoElement} controls muted playsinline preload="none" poster={video.poster.src}></video>
  {:else}
    <button type="button" class="video-player-facade" aria-label={i18n.t('video.play', { title: video.title })} onclick={() => (playRequested = true)}>
      <img class="video-player-poster" src={video.poster.src} srcset={video.poster.srcset} {sizes} width={video.poster.width} height={video.poster.height} alt="" loading={priority ? 'eager' : 'lazy'} fetchpriority={priority ? 'high' : undefined} />
      <span class="video-player-play"><Icon icon={faPlay} /></span>
      <span class="video-player-meta">
        <span class="video-player-title">{video.title}</span>
        <span class="video-player-duration">{formatDuration(video.duration)}</span>
      </span>
    </button>
  {/if}
</div>

<style>
  .video-player {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background-color: var(--color-black);
    color: var(--color-white);

    @media (--tablet) {
      border-radius: var(--radius-3);
    }
  }

  .video-player-video {
    width: 100%;
    height: 100%;
  }

  .video-player-facade {
    position: absolute;
    inset: 0;
    display: grid;
    align-content: end;
    padding: var(--space-7);
    border: none;
    background: none;
    color: inherit;
    font: inherit;
    text-align: start;
    cursor: pointer;

    &:focus-visible {
      outline: var(--border-3) solid var(--color-variant-brand-line);
      outline-offset: calc(var(--border-3) * -1);
    }
  }

  .video-player-poster {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-player-play {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    width: var(--space-16);
    height: var(--space-16);
    margin: auto;
    border-radius: var(--radius-6);
    background-color: var(--color-white);
    color: var(--color-heading);
    font-size: var(--font-size-6);
    box-shadow: 0 var(--space-2) var(--space-8) var(--shadow-2);
  }

  .video-player-meta {
    position: relative;
    display: grid;
    gap: var(--space-2);
    margin: calc(var(--space-7) * -1);
    padding: var(--space-12) var(--space-7) var(--space-7);
    background: linear-gradient(transparent, var(--shadow-2));
  }

  .video-player-title {
    font-size: var(--font-size-6);
    font-weight: var(--font-weight-3);
    line-height: var(--line-height-2);
    text-wrap: balance;
  }

  .video-player-duration {
    font-size: var(--font-size-3);
    font-variant-numeric: tabular-nums;
    opacity: var(--opacity-4);
  }
</style>
